import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import useImageLoader from './useImageLoader';
import LocalOpeningRollModal from './LocalOpeningRollModal';
import {
  createInitialState,
  rollDice,
  calculateLegalMoves,
  executeMove,
  getMovesForPiece,
  getMovesForDestination,
  endTurn,
  getMovablePoints,
} from './gameEngine';

const CANVAS_W = 900;
const CANVAS_H = 900;
const DEFAULT_CHECKER_SIZE = 56;
const POINT_STACK_HEIGHT = 336;
const CPU_ROLL_DELAY_MS = 1100;
const CPU_MOVE_DELAY_MS = 900;
const MODE_SELECT_MOBILE_MAX_WIDTH_PX = 640;
const IS_MOBILE_MODE_SELECT_UI = typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia(`(max-width: ${MODE_SELECT_MOBILE_MAX_WIDTH_PX}px)`).matches;
const MODE_SELECT_Y_SHIFT = IS_MOBILE_MODE_SELECT_UI ? -70 : -90;
const MODE_SELECT_BTN_HEIGHT = IS_MOBILE_MODE_SELECT_UI ? 86 : 65;
const MODE_SELECT_BTN_GAP = IS_MOBILE_MODE_SELECT_UI ? 20 : 25;
const MODE_SELECT_BTN_X1 = 250;
const MODE_SELECT_BTN_X2 = 650;
const MODE_SELECT_TOP_CENTER_Y = 532.5 + MODE_SELECT_Y_SHIFT;
const MODE_SELECT_STEP_Y = MODE_SELECT_BTN_HEIGHT + MODE_SELECT_BTN_GAP;
const MODE_ONLINE_CENTER_Y = MODE_SELECT_TOP_CENTER_Y;
const MODE_CPU_CENTER_Y = MODE_SELECT_TOP_CENTER_Y + MODE_SELECT_STEP_Y;
const MODE_LOCAL_CENTER_Y = MODE_SELECT_TOP_CENTER_Y + 2 * MODE_SELECT_STEP_Y;
const MODE_LOCAL_BTN = {
  x1: MODE_SELECT_BTN_X1,
  x2: MODE_SELECT_BTN_X2,
  y1: MODE_LOCAL_CENTER_Y - MODE_SELECT_BTN_HEIGHT / 2,
  y2: MODE_LOCAL_CENTER_Y + MODE_SELECT_BTN_HEIGHT / 2,
};
const MODE_CPU_BTN = {
  x1: MODE_SELECT_BTN_X1,
  x2: MODE_SELECT_BTN_X2,
  y1: MODE_CPU_CENTER_Y - MODE_SELECT_BTN_HEIGHT / 2,
  y2: MODE_CPU_CENTER_Y + MODE_SELECT_BTN_HEIGHT / 2,
};
const MODE_ONLINE_BTN = {
  x1: MODE_SELECT_BTN_X1,
  x2: MODE_SELECT_BTN_X2,
  y1: MODE_ONLINE_CENTER_Y - MODE_SELECT_BTN_HEIGHT / 2,
  y2: MODE_ONLINE_CENTER_Y + MODE_SELECT_BTN_HEIGHT / 2,
};
const NO_MOVE_DELAY_MS = 10000;

function getOpponent(color) {
  return color === 'white' ? 'black' : 'white';
}

function cloneState(state) {
  return JSON.parse(JSON.stringify(state));
}

function pipCount(state, color) {
  const barPenalty = (state?.bar?.[color]?.length || 0) * 25;
  let total = barPenalty;

  for (let point = 1; point <= 24; point += 1) {
    const pieces = state?.points?.[point]?.pieces || [];
    const ownCount = pieces.filter((piece) => piece.color === color).length;
    if (!ownCount) continue;
    const distance = color === 'white' ? point : 25 - point;
    total += ownCount * distance;
  }

  return total;
}

function countBlots(state, color) {
  let blots = 0;
  for (let point = 1; point <= 24; point += 1) {
    const pieces = state?.points?.[point]?.pieces || [];
    if (pieces.length === 1 && pieces[0]?.color === color) {
      blots += 1;
    }
  }
  return blots;
}

function countMadePoints(state, color) {
  let made = 0;
  for (let point = 1; point <= 24; point += 1) {
    const pieces = state?.points?.[point]?.pieces || [];
    if (pieces.length >= 2 && pieces[0]?.color === color) {
      made += 1;
    }
  }
  return made;
}

function countHomeBoardPoints(state, color) {
  const [start, end] = color === 'white' ? [1, 6] : [19, 24];
  let made = 0;
  for (let point = start; point <= end; point += 1) {
    const pieces = state?.points?.[point]?.pieces || [];
    if (pieces.length >= 2 && pieces[0]?.color === color) {
      made += 1;
    }
  }
  return made;
}

function countAnchors(state, color) {
  const [start, end] = color === 'white' ? [19, 24] : [1, 6];
  let anchors = 0;
  for (let point = start; point <= end; point += 1) {
    const pieces = state?.points?.[point]?.pieces || [];
    if (pieces.length >= 2 && pieces[0]?.color === color) {
      anchors += 1;
    }
  }
  return anchors;
}

function countPiecesInZone(state, color, start, end) {
  let total = 0;
  for (let point = start; point <= end; point += 1) {
    const pieces = state?.points?.[point]?.pieces || [];
    total += pieces.filter((piece) => piece.color === color).length;
  }
  return total;
}

function blotExposure(state, color) {
  const enemy = getOpponent(color);
  let exposure = 0;

  for (let target = 1; target <= 24; target += 1) {
    const pieces = state?.points?.[target]?.pieces || [];
    if (pieces.length !== 1 || pieces[0]?.color !== color) continue;

    let threats = 0;
    for (let die = 1; die <= 6; die += 1) {
      const source = enemy === 'white' ? target + die : target - die;
      if (source >= 1 && source <= 24) {
        const sourcePieces = state?.points?.[source]?.pieces || [];
        if (sourcePieces.some((piece) => piece.color === enemy)) {
          threats += 1;
        }
      }
    }

    if (state?.bar?.[enemy]?.length) {
      const entryPoint = enemy === 'white' ? 25 - target : target;
      if (entryPoint >= 1 && entryPoint <= 6) {
        threats += 1;
      }
    }

    exposure += threats;
  }

  return exposure;
}

function longestPrime(state, color) {
  let longest = 0;
  let current = 0;
  for (let point = 1; point <= 24; point += 1) {
    const pieces = state?.points?.[point]?.pieces || [];
    if (pieces.length >= 2 && pieces[0]?.color === color) {
      current += 1;
      longest = Math.max(longest, current);
    } else {
      current = 0;
    }
  }
  return longest;
}

function furthestBackPoint(state, color) {
  if (color === 'white') {
    for (let point = 24; point >= 1; point -= 1) {
      const pieces = state?.points?.[point]?.pieces || [];
      if (pieces.some((piece) => piece.color === color)) return point;
    }
    return 0;
  }

  for (let point = 1; point <= 24; point += 1) {
    const pieces = state?.points?.[point]?.pieces || [];
    if (pieces.some((piece) => piece.color === color)) return point;
  }
  return 25;
}

function hasContact(state) {
  const whiteBack = furthestBackPoint(state, 'white');
  const blackBack = furthestBackPoint(state, 'black');
  return whiteBack > blackBack;
}

function countCheckersInHomeBoard(state, color) {
  return color === 'white'
    ? countPiecesInZone(state, color, 1, 6)
    : countPiecesInZone(state, color, 19, 24);
}

function getGameStage(state, color, opponent) {
  const ownOff = state?.borneOff?.[color]?.length || 0;
  const oppOff = state?.borneOff?.[opponent]?.length || 0;
  const ownHomeCheckers = countCheckersInHomeBoard(state, color);
  const oppHomeCheckers = countCheckersInHomeBoard(state, opponent);

  if (ownOff >= 8 || oppOff >= 8 || ownHomeCheckers >= 10 || oppHomeCheckers >= 10) {
    return 'bearoff';
  }

  if (!hasContact(state)) {
    return 'race';
  }

  return 'contact';
}

function createTurnState(state, player, dice) {
  const used = dice[0] === dice[1]
    ? [false, false, false, false]
    : [false, false];
  const next = {
    ...cloneState(state),
    currentPlayer: player,
    dice: [...dice],
    diceRolled: true,
    diceUsed: used,
    selectedPoint: null,
    noMoveMessage: null,
    turnComplete: false,
  };
  next.legalMoves = calculateLegalMoves(next);
  if (next.legalMoves.length === 0) {
    next.noMoveMessage = player;
    next.turnComplete = true;
  }
  return next;
}

function getDiceOutcomes() {
  const outcomes = [];
  for (let d1 = 1; d1 <= 6; d1 += 1) {
    for (let d2 = d1; d2 <= 6; d2 += 1) {
      outcomes.push({
        dice: [d1, d2],
        weight: d1 === d2 ? 1 : 2,
      });
    }
  }
  return outcomes;
}

function getBoardKey(state) {
  const pointsKey = Array.from({ length: 24 }, (_, idx) => {
    const point = state?.points?.[idx + 1]?.pieces || [];
    if (!point.length) return '.';
    return `${point[0]?.color?.[0] || 'x'}${point.length}`;
  }).join('|');
  const barKey = `bw${state?.bar?.white?.length || 0}bb${state?.bar?.black?.length || 0}`;
  const offKey = `ow${state?.borneOff?.white?.length || 0}ob${state?.borneOff?.black?.length || 0}`;
  const diceKey = `${state?.currentPlayer || '-'}:${(state?.dice || []).join(',')}:${(state?.diceUsed || []).map(Boolean).map((v) => (v ? 1 : 0)).join('')}`;
  return `${pointsKey}#${barKey}#${offKey}#${diceKey}`;
}

function evaluateBoard(state, color) {
  const opponent = getOpponent(color);
  const ownBar = state?.bar?.[color]?.length || 0;
  const oppBar = state?.bar?.[opponent]?.length || 0;
  const ownOff = state?.borneOff?.[color]?.length || 0;
  const oppOff = state?.borneOff?.[opponent]?.length || 0;
  const ownBlots = countBlots(state, color);
  const oppBlots = countBlots(state, opponent);
  const ownMadePoints = countMadePoints(state, color);
  const oppMadePoints = countMadePoints(state, opponent);
  const ownHomePoints = countHomeBoardPoints(state, color);
  const oppHomePoints = countHomeBoardPoints(state, opponent);
  const ownAnchors = countAnchors(state, color);
  const oppAnchors = countAnchors(state, opponent);
  const ownPips = pipCount(state, color);
  const oppPips = pipCount(state, opponent);
  const ownExposure = blotExposure(state, color);
  const oppExposure = blotExposure(state, opponent);
  const ownPrime = longestPrime(state, color);
  const oppPrime = longestPrime(state, opponent);
  const ownHomeCheckers = color === 'white'
    ? countPiecesInZone(state, color, 1, 6)
    : countPiecesInZone(state, color, 19, 24);
  const oppHomeCheckers = opponent === 'white'
    ? countPiecesInZone(state, opponent, 1, 6)
    : countPiecesInZone(state, opponent, 19, 24);
  const stage = getGameStage(state, color, opponent);

  let score = 0;
  score += (ownOff - oppOff) * 140;
  score += (oppBar - ownBar) * 55;
  score += (ownMadePoints - oppMadePoints) * 16;
  score += (ownHomePoints - oppHomePoints) * 20;
  score += (ownAnchors - oppAnchors) * 11;
  score += (oppBlots - ownBlots) * 9;
  score += (oppExposure - ownExposure) * 12;
  score += (ownPrime - oppPrime) * 18;
  score += (ownHomeCheckers - oppHomeCheckers) * 4;
  score += (oppPips - ownPips) * 2.4;

  if (stage === 'contact') {
    score += (oppBar - ownBar) * 18;
    score += (ownPrime - oppPrime) * 20;
    score += (ownAnchors - oppAnchors) * 10;
    score += (oppExposure - ownExposure) * 15;
    score -= ownBlots * 8;
  } else if (stage === 'race') {
    score += (oppPips - ownPips) * 4.5;
    score += (ownOff - oppOff) * 24;
    score -= ownBlots * 3;
    score += ownHomeCheckers * 2;
  } else if (stage === 'bearoff') {
    score += (ownOff - oppOff) * 38;
    score += (oppPips - ownPips) * 5.5;
    score += ownHomeCheckers * 3;
    score -= ownBar * 25;
  }

  if (state?.gamePhase === 'gameOver') {
    if (state.winner === color) score += 100000;
    if (state.winner === opponent) score -= 100000;
  }

  return score;
}

function getBestComputerMove(state, color) {
  const diceOutcomes = getDiceOutcomes();
  const cache = new Map();
  const rootLegalCount = state?.legalMoves?.length || 0;
  const searchDepth = rootLegalCount >= 10 ? 2 : 3;
  const rootBranchLimit = rootLegalCount >= 10 ? 10 : 14;
  const deepBranchLimit = rootLegalCount >= 10 ? 6 : 9;
  const timeBudgetMs = rootLegalCount >= 10 ? 140 : 220;
  const searchStartedAt = performance.now();

  function isOutOfTime() {
    return performance.now() - searchStartedAt >= timeBudgetMs;
  }

  function immediateMoveBonus(move, playerColor, nextState = null) {
    let bonus = 0;
    const opponent = getOpponent(playerColor);
    const stage = getGameStage(nextState || state, playerColor, opponent);
    if (move.isHit) {
      bonus += stage === 'contact' ? 95 : 68;
      if (typeof move.to === 'number') {
        const isHomeBoardHit = playerColor === 'white' ? move.to <= 6 : move.to >= 19;
        if (isHomeBoardHit) bonus += 22;
      }
      if (nextState) {
        const oppBarAfterHit = nextState?.bar?.[opponent]?.length || 0;
        const ownExposureAfterHit = blotExposure(nextState, playerColor);
        const oppExposureAfterHit = blotExposure(nextState, opponent);
        bonus += oppBarAfterHit * 10;
        bonus += Math.max(0, oppExposureAfterHit - ownExposureAfterHit) * 4;
      }
    }
    if (move.to === 'off') bonus += 34;

    if (typeof move.to === 'number') {
      const ownHomeEntry = playerColor === 'white' ? move.to <= 6 : move.to >= 19;
      if (ownHomeEntry) bonus += 8;
    }

    if (typeof move.from === 'number' && typeof move.to === 'number') {
      const progress = playerColor === 'white' ? move.from - move.to : move.to - move.from;
      bonus += progress * 1.5;
    }

    if (nextState && typeof move.to === 'number') {
      const ownExposureAfterMove = blotExposure(nextState, playerColor);
      const currentExposure = blotExposure(state, playerColor);
      bonus -= Math.max(0, ownExposureAfterMove - currentExposure) * (move.isHit ? 3.5 : 5.5);
    }

    return bonus;
  }

  function enumerateTurnSequences(turnState, playerColor, sequence = [], depth = 0) {
    const legalMoves = turnState?.legalMoves || [];
    if (!legalMoves.length || turnState.turnComplete || turnState.gamePhase === 'gameOver') {
      return [{
        state: turnState,
        sequence,
        score: evaluateBoard(turnState, color),
      }];
    }
    if (isOutOfTime()) {
      return [{
        state: turnState,
        sequence,
        score: evaluateBoard(turnState, color),
      }];
    }

    const rankedMoves = [...legalMoves]
      .map((move) => {
        const nextState = executeMove(cloneState(turnState), move);
        return {
          move,
          nextState,
          rank: evaluateBoard(nextState, color) + immediateMoveBonus(move, playerColor, nextState),
        };
      })
      .sort((a, b) => b.rank - a.rank)
      .slice(0, depth === 0 ? rootBranchLimit : deepBranchLimit);

    const sequences = [];
    rankedMoves.forEach(({ move, nextState, rank }) => {
      const branches = enumerateTurnSequences(nextState, playerColor, [...sequence, move], depth + 1);
      branches.forEach((branch) => {
        sequences.push({
          ...branch,
          score: branch.score + rank * 0.08,
        });
      });
    });

    return sequences.sort((a, b) => b.score - a.score).slice(0, depth === 0 ? rootBranchLimit + 2 : deepBranchLimit + 2);
  }

  function evaluateFuture(turnEndState, depthLeft) {
    if (depthLeft <= 0 || turnEndState.gamePhase === 'gameOver') {
      return evaluateBoard(turnEndState, color);
    }
    if (isOutOfTime()) {
      return evaluateBoard(turnEndState, color);
    }

    const nextPlayer = getOpponent(turnEndState.currentPlayer);
    const postTurnState = endTurn(cloneState(turnEndState));
    const cacheKey = `${depthLeft}:${nextPlayer}:${getBoardKey(postTurnState)}`;
    if (cache.has(cacheKey)) return cache.get(cacheKey);

    let weightedScore = 0;
    let totalWeight = 0;
    const sampledScores = [];

    diceOutcomes.forEach(({ dice, weight }) => {
      const rolledState = createTurnState(postTurnState, nextPlayer, dice);

      let outcomeScore;
      if (rolledState.turnComplete || rolledState.gamePhase === 'gameOver') {
        outcomeScore = rolledState.gamePhase === 'gameOver'
          ? evaluateBoard(rolledState, color)
          : evaluateFuture(rolledState, depthLeft - 1);
      } else {
        const sequences = enumerateTurnSequences(rolledState, nextPlayer);
        const bestSequence = sequences.reduce((best, candidate) => {
          if (!best) return candidate;
          return nextPlayer === color
            ? (candidate.score > best.score ? candidate : best)
            : (candidate.score < best.score ? candidate : best);
        }, null);

        outcomeScore = bestSequence
          ? evaluateFuture(bestSequence.state, depthLeft - 1)
          : evaluateBoard(rolledState, color);
      }

      weightedScore += outcomeScore * weight;
        totalWeight += weight;
      sampledScores.push({ score: outcomeScore, weight });
    });

    const averageScore = totalWeight ? weightedScore / totalWeight : evaluateBoard(postTurnState, color);
    const weightedExtremum = (() => {
      if (!sampledScores.length) return averageScore;
      const sorted = [...sampledScores].sort((a, b) =>
        nextPlayer === color ? b.score - a.score : a.score - b.score
      );
      let remaining = Math.max(2, Math.ceil(totalWeight * 0.22));
      let bucketWeight = 0;
      let bucketScore = 0;
      for (const sample of sorted) {
        if (remaining <= 0) break;
        const take = Math.min(sample.weight, remaining);
        bucketWeight += take;
        bucketScore += sample.score * take;
        remaining -= take;
      }
      return bucketWeight ? bucketScore / bucketWeight : averageScore;
    })();

    const result = nextPlayer === color
      ? (averageScore * 0.48) + (weightedExtremum * 0.52)
      : (averageScore * 0.22) + (weightedExtremum * 0.78);
    cache.set(cacheKey, result);
    return result;
  }

  const turnSequences = enumerateTurnSequences(state, color);
  if (!turnSequences.length) return state?.legalMoves?.[0] || null;

  const bestTurn = turnSequences.reduce((best, candidate) => {
    const futureScore = isOutOfTime()
      ? evaluateBoard(candidate.state, color)
      : evaluateFuture(candidate.state, searchDepth);
    const totalScore = candidate.score * 0.2 + futureScore * 0.8;
    if (!best || totalScore > best.totalScore) {
      return { totalScore, sequence: candidate.sequence };
    }
    return best;
  }, null);

  if (bestTurn?.sequence?.[0]) return bestTurn.sequence[0];

  const fallbackMove = [...(state?.legalMoves || [])]
    .map((move) => {
      const nextState = executeMove(cloneState(state), move);
      return {
        move,
        score: evaluateBoard(nextState, color) + immediateMoveBonus(move, color, nextState),
      };
    })
    .sort((a, b) => b.score - a.score)[0];

  return fallbackMove?.move || null;
}

// Position function matching the Python Pygame code exactly
// Position function matching the Python Pygame code exactly
function position(x, y) {
  let cx = 88;
  let cy = 161;
  let px = x;
  if (px >= 6) px += 1;
  if (y >= 6) cy += 22;
  return [cx + px * 56, cy + y * 56];
}

// Convert board point index (1-24) to canvas pixel positions for each slot in the stack
function getPointStackLayout(location, pieceCount = 0) {
  const posX = location < 13 ? 12 - location : location - 1 - 12;
  const [baseX] = position(posX, location < 13 ? 0 : 11);
  const safeCount = Math.max(1, pieceCount);
  const pieceSize = Math.min(DEFAULT_CHECKER_SIZE, Math.floor(POINT_STACK_HEIGHT / safeCount));
  const positions = [];

  if (location < 13) {
    const startY = position(posX, 0)[1];
    for (let i = 0; i < pieceCount; i += 1) {
      positions.push([baseX, startY + (i * pieceSize)]);
    }
  } else {
    const bottomY = position(posX, 11)[1];
    for (let i = 0; i < pieceCount; i += 1) {
      positions.push([baseX, bottomY - (i * pieceSize)]);
    }
  }

  return { positions, pieceSize };
}

// Bar positions for white captured pieces (bottom-center area)
function getBarPositionsWhite() {
  const positions = [];
  for (let i = 0; i < 6; i++) {
    positions.push([426, 450 + i * 56]);
  }
  return positions;
}

// Bar positions for black captured pieces (top-center area)
function getBarPositionsBlack() {
  const positions = [];
  for (let i = 0; i < 6; i++) {
    positions.push([426, 161 + i * 56]);
  }
  return positions;
}

// Bearing off positions
function getBearingOffPositions(color) {
  const positions = [];
  const bx = 840;
  const by = color === 'white' ? 595 : 159;
  if (color === 'white') {
    for (let i = 0; i < 15; i++) positions.push([bx, by + 18 * i]);
  } else {
    for (let i = 14; i >= 0; i--) positions.push([bx, by + 18 * i]);
  }
  return positions;
}

// Get position for the destination light overlay on a column
function getPointLightPosition(location) {
  if (location < 13) {
    return position(12 - location, 0);
  } else {
    return position(location - 1 - 12, 7);
  }
}

function clampStackIndex(index) {
  return Math.max(0, index);
}

function getVisibleStackWindow(pieceCount) {
  return { startIndex: 0, visibleCount: pieceCount };
}

function drawWrappedCanvasText(ctx, text, x, startY, maxWidth, lineHeight) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth || !currentLine) {
      currentLine = candidate;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  lines.forEach((line, index) => {
    ctx.fillText(line, x, startY + (index * lineHeight));
  });

  return lines.length;
}

function getVisibleDiceState(gameState) {
  const gs = gameState || {};
  const dice = Array.isArray(gs.dice) ? gs.dice : [];
  const diceUsed = Array.isArray(gs.diceUsed) ? gs.diceUsed : [];
  const isDouble = dice.length >= 2 && dice[0] === dice[1];
  const remainingMoves = isDouble
    ? diceUsed.filter((used) => !used).length
    : diceUsed.slice(0, 2).filter((used) => !used).length;

  return {
    first: Number(dice[0] || 0),
    second: Number(dice[1] || 0),
    isDouble,
    firstUsed: !isDouble && Boolean(diceUsed[0]),
    secondUsed: !isDouble && Boolean(diceUsed[1]),
    remainingMoves,
  };
}

function renderDieFace(value) {
  const safe = Math.max(1, Math.min(6, Number(value) || 1));
  const positionsByValue = {
    1: [[50, 50]],
    2: [[25, 25], [75, 75]],
    3: [[25, 25], [50, 50], [75, 75]],
    4: [[25, 25], [75, 25], [25, 75], [75, 75]],
    5: [[25, 25], [75, 25], [50, 50], [25, 75], [75, 75]],
    6: [[25, 25], [75, 25], [25, 50], [75, 50], [25, 75], [75, 75]],
  };

  return positionsByValue[safe].map(([col, row], index) => (
    <span
      key={`${safe}-${col}-${row}-${index}`}
      style={{
        ...styles.dicePip,
        left: `calc(${col}% - 4px)`,
        top: `calc(${row}% - 4px)`,
      }}
    />
  ));
}

export default function BackgammonGame({
  onSelectOnlineMode,
  onSelectTournamentMode,
  onMatchCompleted,
  allowTournamentMode = true,
  allowOnlineMode = true,
  onlineModeBlockedMessage = '',
  onExit,
  onGamePhaseChange = null,
  isFullscreen = false,
  onToggleFullscreen = null,
}) {
  const canvasRef = useRef(null);
  const { images, loaded } = useImageLoader();

  const [gameState, setGameState] = useState(() => ({
    ...createInitialState(),
    gamePhase: 'modeSelect',
  }));
  const [hoverArea, setHoverArea] = useState(null);
  const [playMode, setPlayMode] = useState(null); // 'local' | 'vsComputer' | 'online'
  const [modeNotice, setModeNotice] = useState(null);

  // Turn rolling animation state
  const [turnRolling, setTurnRolling] = useState(false);
  const [turnRollCount, setTurnRollCount] = useState(0);
  const [turnDiceW, setTurnDiceW] = useState(1);
  const [turnDiceB, setTurnDiceB] = useState(1);
  const [turnRollResult, setTurnRollResult] = useState(null);

  // UI state
  const [selectedFrom, setSelectedFrom] = useState(null); // point index, 'bar', or null
  const [noMoveMsg, setNoMoveMsg] = useState(null);
  const [firstMoveMsg, setFirstMoveMsg] = useState(null);
  const [moveAnimation, setMoveAnimation] = useState(null);
  const [showQuitConfirm, setShowQuitConfirm] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));
  const [diceTrayRolling, setDiceTrayRolling] = useState(false);
  const [lastShownDice, setLastShownDice] = useState({ first: 1, second: 1, hasValue: false });
  const resultReportedRef = useRef(false);
  const previousDiceRolledRef = useRef(false);

  const animFrameRef = useRef(null);
  const humanColor = playMode === 'vsComputer' ? 'black' : null;
  const computerColor = playMode === 'vsComputer' ? 'white' : null;
  const isMobileViewport = viewportWidth < 900;
  const diceState = getVisibleDiceState(gameState);
  const canRollDice = Boolean(
    playMode === 'vsComputer'
    && gameState?.gamePhase === 'playing'
    && gameState?.currentPlayer === humanColor
    && !gameState?.diceRolled
  );
  const visibleDiceValues = gameState?.diceRolled
    ? { first: diceState.first, second: diceState.second, blurred: false }
    : {
        first: lastShownDice.first,
        second: lastShownDice.second,
        blurred: canRollDice || lastShownDice.hasValue,
      };
  const diceTrayTitle = !gameState?.diceRolled
    ? (canRollDice ? 'Roll Dice' : 'Waiting For Roll')
    : (diceState.isDouble ? `Double ${diceState.first}` : 'Current Dice');
  const isAiPlayingScreen = playMode === 'vsComputer' && gameState?.gamePhase === 'playing';
  const turnLabel = gameState?.currentPlayer === humanColor
    ? 'Your Turn'
    : gameState?.currentPlayer === computerColor
      ? 'AI Turn'
      : 'Waiting';

  const getCheckerCoords = useCallback((state, color, location, stackIndex = 0) => {
    if (location === 'bar') {
      const positions = color === 'white' ? getBarPositionsWhite() : getBarPositionsBlack();
      const [x, y] = positions[clampStackIndex(stackIndex)] || positions[0];
      return [x, y, DEFAULT_CHECKER_SIZE];
    }
    if (location === 'off') {
      const positions = getBearingOffPositions(color);
      const [x, y] = positions[Math.min(stackIndex, positions.length - 1)] || positions[0];
      return [x, y, DEFAULT_CHECKER_SIZE];
    }
    const pointCount = state?.points?.[location]?.pieces?.length || 0;
    const { positions, pieceSize } = getPointStackLayout(location, pointCount);
    const [x, y] = positions[clampStackIndex(stackIndex)] || positions[0] || [0, 0];
    return [x, y, pieceSize];
  }, []);

  const inferMoveAnimation = useCallback((prevState, nextState) => {
    if (!prevState || !nextState) return null;
    const color = prevState.currentPlayer;
    if (!color) return null;

    const pointDiffs = [];
    for (let loc = 1; loc <= 24; loc += 1) {
      const prevCount = prevState.points?.[loc]?.pieces?.filter((piece) => piece.color === color).length || 0;
      const nextCount = nextState.points?.[loc]?.pieces?.filter((piece) => piece.color === color).length || 0;
      if (prevCount !== nextCount) pointDiffs.push({ loc, prevCount, nextCount });
    }

    const prevBarCount = prevState.bar?.[color]?.length || 0;
    const nextBarCount = nextState.bar?.[color]?.length || 0;
    const prevOffCount = prevState.borneOff?.[color]?.length || 0;
    const nextOffCount = nextState.borneOff?.[color]?.length || 0;

    let from = null;
    let to = null;
    let fromStackIndex = 0;
    let toStackIndex = 0;

    if (prevBarCount > nextBarCount) {
      from = 'bar';
      fromStackIndex = prevBarCount - 1;
    } else {
      const sourcePoint = pointDiffs.find((entry) => entry.prevCount > entry.nextCount);
      if (sourcePoint) {
        from = sourcePoint.loc;
        fromStackIndex = sourcePoint.prevCount - 1;
      }
    }

    if (nextOffCount > prevOffCount) {
      to = 'off';
      toStackIndex = nextOffCount - 1;
    } else {
      const targetPoint = pointDiffs.find((entry) => entry.nextCount > entry.prevCount);
      if (targetPoint) {
        to = targetPoint.loc;
        toStackIndex = targetPoint.nextCount - 1;
      }
    }

    if (from === null || to === null) return null;

    const [startX, startY, startSize] = getCheckerCoords(prevState, color, from, fromStackIndex);
    const [endX, endY, endSize] = getCheckerCoords(nextState, color, to, toStackIndex);

    return {
      color,
      to,
      startX,
      startY,
      startSize,
      endX,
      endY,
      endSize,
      startAt: performance.now(),
      duration: 650,
    };
  }, [getCheckerCoords]);

  const applyAnimatedMove = useCallback((prevState, move) => {
    const nextState = executeMove(prevState, move);
    setMoveAnimation(inferMoveAnimation(prevState, nextState));
    return nextState;
  }, [inferMoveAnimation]);

  // ---- Turn rolling animation ----
  useEffect(() => {
    if (!turnRolling) return;
    if (turnRollCount < 20) {
      const timer = setTimeout(() => {
        setTurnDiceW(Math.floor(Math.random() * 6) + 1);
        setTurnDiceB(Math.floor(Math.random() * 6) + 1);
        setTurnRollCount(c => c + 1);
      }, 80);
      return () => clearTimeout(timer);
    } else {
      // Done animating
      if (turnDiceW !== turnDiceB) {
        const first = turnDiceB > turnDiceW ? 'black' : 'white';
        setTurnRolling(false);
        setTurnRollResult({ first });
      } else {
        // Tie, re-roll
        setTurnRollCount(0);
      }
    }
  }, [turnRolling, turnRollCount, turnDiceW, turnDiceB]);

  useEffect(() => {
    if (!turnRollResult) return undefined;
    const timer = setTimeout(() => {
      const { first } = turnRollResult;
      setGameState(prev => {
        const next = {
          ...prev,
          currentPlayer: first,
          gamePhase: 'playing',
          dice: [0, 0],
          diceRolled: false,
          diceUsed: [false, false],
          noMoveMessage: null,
          turnComplete: false,
          legalMoves: [],
        };
        return next;
      });
      setFirstMoveMsg(turnRollResult.first);
      setTimeout(() => setFirstMoveMsg(null), 2000);
      setTurnRollResult(null);
    }, 5000);
    return () => clearTimeout(timer);
  }, [turnRollResult]);

  // ---- Auto end-turn when turn is complete ----
  useEffect(() => {
    if (!gameState.turnComplete || gameState.gamePhase !== 'playing') return;
    if (moveAnimation) return;
    if (gameState.noMoveMessage) {
      setNoMoveMsg(gameState.noMoveMessage);
      const timer = setTimeout(() => {
        setNoMoveMsg(null);
        setGameState(prev => endTurn(prev));
        setSelectedFrom(null);
      }, NO_MOVE_DELAY_MS);
      return () => clearTimeout(timer);
    } else {
      setGameState(prev => endTurn(prev));
      setSelectedFrom(null);
    }
  }, [gameState.turnComplete, gameState.noMoveMessage, gameState.gamePhase, moveAnimation]);

  useEffect(() => {
    if (gameState.gamePhase !== 'gameOver') {
      resultReportedRef.current = false;
      return;
    }
    if (resultReportedRef.current) return;
    resultReportedRef.current = true;

    if (typeof onMatchCompleted === 'function') {
      const mode = playMode === 'vsComputer' ? 'vsComputer' : 'yourself';
      const result = playMode === 'vsComputer'
        ? (gameState.winner === humanColor ? 'win' : 'loss')
        : (gameState.winner ? 'win' : 'draw');
      onMatchCompleted({
        mode,
        result,
        metadata: {
          winnerColor: gameState.winner,
          winType: gameState.winType || null,
          winPoints: gameState.winPoints || 0,
        },
      });
    }
  }, [gameState, onMatchCompleted, playMode, humanColor]);

  useEffect(() => {
    if (!moveAnimation) return undefined;
    const timer = setTimeout(() => setMoveAnimation(null), moveAnimation.duration);
    return () => clearTimeout(timer);
  }, [moveAnimation]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!diceTrayRolling) return undefined;
    const timer = setTimeout(() => setDiceTrayRolling(false), 900);
    return () => clearTimeout(timer);
  }, [diceTrayRolling]);

  useEffect(() => {
    if (gameState?.diceRolled && diceState.first > 0 && diceState.second > 0) {
      setLastShownDice({
        first: diceState.first,
        second: diceState.second,
        hasValue: true,
      });
    }
  }, [gameState?.diceRolled, diceState.first, diceState.second]);

  useEffect(() => {
    previousDiceRolledRef.current = Boolean(gameState?.diceRolled);
  }, [gameState?.diceRolled]);

  const resetDiceTrayState = useCallback(() => {
    setDiceTrayRolling(false);
    setLastShownDice({ first: 1, second: 1, hasValue: false });
    previousDiceRolledRef.current = false;
  }, []);

  const handleRollAction = useCallback(() => {
    if (!canRollDice) return;
    setDiceTrayRolling(true);
    setFirstMoveMsg(null);
    setGameState((prev) => {
      if (
        playMode !== 'vsComputer'
        || prev.gamePhase !== 'playing'
        || prev.currentPlayer !== humanColor
        || prev.diceRolled
      ) {
        return prev;
      }
      const newDice = rollDice();
      const used = newDice[0] === newDice[1]
        ? [false, false, false, false]
        : [false, false];
      const next = {
        ...prev,
        dice: newDice,
        diceRolled: true,
        diceUsed: used,
        noMoveMessage: null,
        turnComplete: false,
      };
      next.legalMoves = calculateLegalMoves(next);
      if (next.legalMoves.length === 0) {
        next.noMoveMessage = prev.currentPlayer;
        next.turnComplete = true;
      }
      return next;
    });
  }, [canRollDice, humanColor, playMode]);

  // ---- Computer turn ----
  useEffect(() => {
    if (playMode !== 'vsComputer') return;
    if (gameState.gamePhase !== 'playing') return;
    if (gameState.currentPlayer !== computerColor) return;
    if (gameState.turnComplete) return;
    if (moveAnimation) return;

    // Computer rolls when needed
    if (!gameState.diceRolled) {
      const timer = setTimeout(() => {
        setDiceTrayRolling(true);
        setFirstMoveMsg(null);
        setGameState(prev => {
          if (
            playMode !== 'vsComputer' ||
            prev.gamePhase !== 'playing' ||
            prev.currentPlayer !== computerColor ||
            prev.diceRolled
          ) {
            return prev;
          }
          const newDice = rollDice();
          const used = newDice[0] === newDice[1]
            ? [false, false, false, false]
            : [false, false];
          const next = {
            ...prev,
            dice: newDice,
            diceRolled: true,
            diceUsed: used,
            noMoveMessage: null,
            turnComplete: false,
          };
          next.legalMoves = calculateLegalMoves(next);
          if (next.legalMoves.length === 0) {
            next.noMoveMessage = prev.currentPlayer;
            next.turnComplete = true;
          }
          return next;
        });
      }, CPU_ROLL_DELAY_MS);
      return () => clearTimeout(timer);
    }

    // Computer plays one legal move at a time
    if (gameState.legalMoves.length > 0) {
      const timer = setTimeout(() => {
        setGameState(prev => {
          if (
            playMode !== 'vsComputer' ||
            prev.gamePhase !== 'playing' ||
            prev.currentPlayer !== computerColor ||
            !prev.diceRolled ||
            prev.legalMoves.length === 0
          ) {
            return prev;
          }
          const chosenMove = getBestComputerMove(prev, computerColor);
          if (!chosenMove) {
            return {
              ...prev,
              noMoveMessage: prev.currentPlayer,
              turnComplete: true,
            };
          }
          return applyAnimatedMove(prev, chosenMove);
        });
      }, CPU_MOVE_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [playMode, gameState, computerColor, applyAnimatedMove, moveAnimation]);

  // ---- Compute derived data for rendering ----
  const movablePoints = useMemo(() => gameState.diceRolled ? getMovablePoints(gameState) : new Set(), [gameState]);
  const movesForSelected = useMemo(() => selectedFrom !== null ? getMovesForPiece(gameState, selectedFrom) : [], [selectedFrom, gameState]);
  const destinationPoints = useMemo(() => new Set(movesForSelected.map(m => m.to)), [movesForSelected]);

  // ---- Hit-test helpers ----
  function getCanvasCoords(e) {
    const canvas = canvasRef.current;
    if (!canvas) return [0, 0];
    const rect = canvas.getBoundingClientRect();
    return [
      (e.clientX - rect.left) * (CANVAS_W / rect.width),
      (e.clientY - rect.top) * (CANVAS_H / rect.height),
    ];
  }

  function inRect(mx, my, x1, x2, y1, y2, pad = 0) {
    return (
      mx >= x1 - pad &&
      mx <= x2 + pad &&
      my >= y1 - pad &&
      my <= y2 + pad
    );
  }

  // Find which point (1-24) a click is on, considering piece stack heights
  function hitTestPoint(mx, my) {
    for (let loc = 1; loc <= 24; loc++) {
      const point = gameState.points[loc];
      if (point.pieces.length === 0) continue;
      const { positions, pieceSize } = getPointStackLayout(loc, point.pieces.length);
      const { visibleCount } = getVisibleStackWindow(point.pieces.length);
      // Check from the visible top piece down.
      for (let slot = visibleCount - 1; slot >= 0; slot -= 1) {
        const [px, py] = positions[slot];
        if (inRect(mx, my, px, px + pieceSize, py, py + pieceSize, 4)) {
          return loc;
        }
      }
    }
    return null;
  }

  // Check if clicking on bar
  function hitTestBar(mx, my) {
    const color = gameState.currentPlayer;
    if (!color || gameState.bar[color].length === 0) return false;
    const barPos = color === 'white' ? getBarPositionsWhite() : getBarPositionsBlack();
    for (let i = 0; i < Math.min(gameState.bar[color].length, 6); i++) {
      const [bx, by] = barPos[i];
      if (inRect(mx, my, bx, bx + 56, by, by + 56, 8)) return true;
    }
    return false;
  }

  // Check if clicking a destination column (full column area)
  function isWithinPointColumn(mx, my, point) {
    const lp = getPointLightPosition(point);
    const centerX = lp[0] + 28;
    const halfWidth = 42;
    if (point < 13) {
      // Let users click anywhere inside the upper playable triangle area,
      // not only on the destination light/label.
      return mx >= centerX - halfWidth && mx <= centerX + halfWidth && my >= 145 && my <= 505;
    }
    return mx >= centerX - halfWidth && mx <= centerX + halfWidth && my >= 395 && my <= 830;
  }

  // Detect clicked board point by column area (works even on empty points)
  function hitTestPointColumn(mx, my) {
    for (let point = 1; point <= 24; point++) {
      if (isWithinPointColumn(mx, my, point)) return point;
    }
    return null;
  }

  function hitTestDestination(mx, my) {
    for (const to of destinationPoints) {
      if (to === 'off') {
        // Bearing off area
        if (gameState.currentPlayer === 'white') {
          if (mx >= 830 && mx <= 900 && my >= 580 && my <= 880) return 'off';
        } else {
          if (mx >= 830 && mx <= 900 && my >= 140 && my <= 450) return 'off';
        }
        continue;
      }
      if (isWithinPointColumn(mx, my, to)) return to;
    }
    return null;
  }

  const resetToModeSelect = useCallback(() => {
    setPlayMode(null);
    setSelectedFrom(null);
    setNoMoveMsg(null);
    setFirstMoveMsg(null);
    setShowQuitConfirm(false);
    setTurnRolling(false);
    setTurnRollCount(0);
    setTurnDiceW(1);
    setTurnDiceB(1);
    setTurnRollResult(null);
    resetDiceTrayState();
    setGameState({
      ...createInitialState(),
      gamePhase: 'modeSelect',
    });
  }, [resetDiceTrayState]);

  const handleBackPress = useCallback(() => {
    if (gameState.gamePhase === 'playing' || gameState.gamePhase === 'turnRoll') {
      setShowQuitConfirm(true);
      return;
    }
    resetToModeSelect();
  }, [gameState.gamePhase, resetToModeSelect]);

  // ---- Click handler ----
  const handleClick = useCallback((e) => {
    const [mx, my] = getCanvasCoords(e);
    const gs = gameState;

    if (gs.gamePhase === 'modeSelect') {
      if (
        allowOnlineMode &&
        inRect(mx, my, MODE_ONLINE_BTN.x1, MODE_ONLINE_BTN.x2, MODE_ONLINE_BTN.y1, MODE_ONLINE_BTN.y2, 14)
      ) {
        if (onlineModeBlockedMessage) {
          setModeNotice(onlineModeBlockedMessage);
          setTimeout(() => setModeNotice(null), 2600);
        } else if (typeof onSelectOnlineMode === 'function') {
          onSelectOnlineMode();
        } else {
          setModeNotice('Online partner mode needs backend and is coming soon.');
          setTimeout(() => setModeNotice(null), 2200);
        }
      } else if (inRect(mx, my, MODE_CPU_BTN.x1, MODE_CPU_BTN.x2, MODE_CPU_BTN.y1, MODE_CPU_BTN.y2, 14)) {
        setPlayMode('vsComputer');
        setGameState(prev => ({ ...prev, gamePhase: 'start' }));
      } else if (allowTournamentMode && inRect(mx, my, MODE_LOCAL_BTN.x1, MODE_LOCAL_BTN.x2, MODE_LOCAL_BTN.y1, MODE_LOCAL_BTN.y2, 14)) {
        if (typeof onSelectTournamentMode === 'function') {
          onSelectTournamentMode();
        } else {
          setModeNotice('Tournament mode is not available right now.');
          setTimeout(() => setModeNotice(null), 2200);
        }
      }
      return;
    }

    if (!playMode) return;
    if (playMode === 'vsComputer' && gs.gamePhase === 'playing' && gs.currentPlayer === computerColor) return;

    // --- Overlay screens ---
    if (gs.gamePhase === 'start') {
      // Start -> go directly to opening roll
      if (inRect(mx, my, 250, 650, 484, 550, 14)) {
        setGameState(prev => ({ ...prev, gamePhase: 'turnRoll' }));
        setTurnRolling(false);
        setTurnRollCount(0);
        setTurnRollResult(null);
        setTurnDiceW(1);
        setTurnDiceB(1);
        resetDiceTrayState();
        return;
      }
      // Quit -> back to mode select
      if (inRect(mx, my, 250, 650, 570, 635, 14)) {
        resetToModeSelect();
        return;
      }
      return;
    }
    if (gs.gamePhase === 'gameOver') {
      if (inRect(mx, my, 250, 650, 484, 550, 14)) {
        setSelectedFrom(null);
        setNoMoveMsg(null);
        setFirstMoveMsg(null);
        setTurnRolling(false);
        setTurnRollCount(0);
        setTurnRollResult(null);
        setTurnDiceW(1);
        setTurnDiceB(1);
        setMoveAnimation(null);
        resetDiceTrayState();
        resultReportedRef.current = false;
        setGameState({
          ...createInitialState(),
          gamePhase: 'turnRoll',
        });
        return;
      }
      if (inRect(mx, my, 250, 650, 570, 635, 14)) {
        resetToModeSelect();
        return;
      }
      return;
    }
    if (gs.gamePhase === 'turnRoll') return;

    // --- Game playing ---
    // Roll dice
    if (!gs.diceRolled) {
      const isWhiteDiceArea = gs.currentPlayer === 'white' && inRect(mx, my, 840, 885, 440, 560, 12);
      const isBlackDiceArea = gs.currentPlayer === 'black' && inRect(mx, my, 3, 48, 420, 540, 12);
      if (isWhiteDiceArea || isBlackDiceArea) {
        handleRollAction();
      }
      return;
    }

    // If a piece is selected, check if clicking a destination
    if (selectedFrom !== null) {
      let destHit = hitTestDestination(mx, my);
      // Fallback: if user clicks directly on a point stack that is a legal destination,
      // treat it as destination even if the column hit-box misses.
      if (destHit === null) {
        const pointHitAsDest = hitTestPoint(mx, my);
        if (pointHitAsDest !== null && destinationPoints.has(pointHitAsDest)) {
          destHit = pointHitAsDest;
        }
      }
      // Fallback: detect by board column for empty or hard-to-hit stacks.
      if (destHit === null) {
        const columnHitAsDest = hitTestPointColumn(mx, my);
        if (columnHitAsDest !== null && destinationPoints.has(columnHitAsDest)) {
          destHit = columnHitAsDest;
        }
      }
      if (destHit !== null) {
        // Find the move for this destination
        const possibleMoves = getMovesForDestination(gs, selectedFrom, destHit);
        if (possibleMoves.length > 0) {
          // Use the first available die for this move
          setGameState(prev => applyAnimatedMove(prev, possibleMoves[0]));
          setSelectedFrom(null);
          return;
        }
      }
    }

    // Check if clicking on bar
    if (hitTestBar(mx, my) && movablePoints.has('bar')) {
      setSelectedFrom('bar');
      return;
    }

    // Check if clicking on a point with a movable piece
    const pointHit = hitTestPoint(mx, my);
    if (pointHit !== null) {
      const point = gs.points[pointHit];
      const topPiece = point.pieces[point.pieces.length - 1];
      if (topPiece && topPiece.color === gs.currentPlayer && movablePoints.has(pointHit)) {
        setSelectedFrom(pointHit);
        return;
      }
    }

    // Fallback source selection by column area.
    const pointColumnHit = hitTestPointColumn(mx, my);
    if (pointColumnHit !== null && movablePoints.has(pointColumnHit)) {
      const point = gs.points[pointColumnHit];
      const topPiece = point.pieces[point.pieces.length - 1];
      if (topPiece && topPiece.color === gs.currentPlayer) {
        setSelectedFrom(pointColumnHit);
        return;
      }
    }

    // Deselect
    setSelectedFrom(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowOnlineMode, allowTournamentMode, gameState, selectedFrom, movablePoints, destinationPoints, playMode, onSelectOnlineMode, onSelectTournamentMode, onlineModeBlockedMessage, computerColor, resetToModeSelect, handleRollAction, resetDiceTrayState]);

  // ---- Mouse move for hover ----
  const handleMouseMove = useCallback((e) => {
    const [mx, my] = getCanvasCoords(e);
    const gs = gameState;
    if (gs.gamePhase !== 'playing') { setHoverArea(null); return; }
    if (gs.currentPlayer === 'white' && !gs.diceRolled) {
      if (inRect(mx, my, 840, 885, 440, 560, 12)) { setHoverArea('whiteDice'); return; }
    }
    if (gs.currentPlayer === 'black' && !gs.diceRolled) {
      if (inRect(mx, my, 3, 48, 420, 540, 12)) { setHoverArea('blackDice'); return; }
    }
    setHoverArea(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState]);

  // ---- Canvas render ----
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !images) return;
    const ctx = canvas.getContext('2d');
    const gs = gameState;

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // Background
    if (images.background) ctx.drawImage(images.background, 0, 0);

    // Draw pieces on points
    for (let loc = 1; loc <= 24; loc++) {
      const point = gs.points[loc];
      const { positions, pieceSize } = getPointStackLayout(loc, point.pieces.length);
      const { startIndex, visibleCount } = getVisibleStackWindow(point.pieces.length);

      for (let slot = 0; slot < visibleCount; slot += 1) {
        const pieceIndex = startIndex + slot;
        const piece = point.pieces[pieceIndex];
        const [px, py] = positions[slot];
        const isTop = pieceIndex === point.pieces.length - 1;
        const hideAnimatedTarget = moveAnimation
          && moveAnimation.to === loc
          && moveAnimation.color === piece.color
          && isTop;

        let img;
        if (piece.color === 'white') {
          if (gs.diceRolled && gs.currentPlayer === 'white' && isTop && movablePoints.has(loc)) {
            img = images.white_highlight;
          } else {
            img = images.white_piece;
          }
        } else {
          if (gs.diceRolled && gs.currentPlayer === 'black' && isTop && movablePoints.has(loc)) {
            img = images.black_highlight;
          } else {
            img = images.black_piece;
          }
        }
        if (img && !hideAnimatedTarget) ctx.drawImage(img, px, py, pieceSize, pieceSize);
      }
    }

    // Draw bar pieces - white
    const barWhitePos = getBarPositionsWhite();
    for (let i = 0; i < gs.bar.white.length && i < 6; i++) {
      const [bx, by] = barWhitePos[i];
      const img = (gs.diceRolled && gs.currentPlayer === 'white') ? images.white_highlight : images.white_piece;
      if (img) ctx.drawImage(img, bx, by);
    }

    // Draw bar pieces - black
    const barBlackPos = getBarPositionsBlack();
    for (let i = 0; i < gs.bar.black.length && i < 6; i++) {
      const [bx, by] = barBlackPos[i];
      const img = (gs.diceRolled && gs.currentPlayer === 'black') ? images.black_highlight : images.black_piece;
      if (img) ctx.drawImage(img, bx, by);
    }

    // Draw borne off pieces
    const whiteOffPos = getBearingOffPositions('white');
    for (let i = 0; i < gs.borneOff.white.length && i < 15; i++) {
      const [ox, oy] = whiteOffPos[i];
      if (images.white_beard_off) ctx.drawImage(images.white_beard_off, ox, oy);
    }
    const blackOffPos = getBearingOffPositions('black');
    for (let i = 0; i < gs.borneOff.black.length && i < 15; i++) {
      const [ox, oy] = blackOffPos[i];
      if (images.black_beard_off) ctx.drawImage(images.black_beard_off, ox, oy);
    }

    if (moveAnimation) {
      const progress = Math.max(0, Math.min(1, (performance.now() - moveAnimation.startAt) / moveAnimation.duration));
      const drawX = moveAnimation.startX + ((moveAnimation.endX - moveAnimation.startX) * progress);
      const drawY = moveAnimation.startY + ((moveAnimation.endY - moveAnimation.startY) * progress);
      const drawSize = moveAnimation.startSize + ((moveAnimation.endSize - moveAnimation.startSize) * progress);
      const img = moveAnimation.color === 'white' ? images.white_piece : images.black_piece;
      if (img) ctx.drawImage(img, drawX, drawY, drawSize, drawSize);
    }

    // Turn light indicator
    if (gs.gamePhase === 'playing' && gs.currentPlayer && images.turn_light) {
      if (gs.currentPlayer === 'white') {
        ctx.drawImage(images.turn_light, 328, 6);
      } else {
        ctx.drawImage(images.turn_light, 490, 6);
      }
    }

    // Dice buttons
    if (gs.gamePhase === 'playing') {
      if (gs.currentPlayer === 'white' && !gs.diceRolled) {
        const img = hoverArea === 'whiteDice' ? images.active_dice_button : images.dice_button;
        if (img) ctx.drawImage(img, 840, 440);
      }
      if (gs.currentPlayer === 'black' && !gs.diceRolled) {
        const img = hoverArea === 'blackDice' ? images.active_player2_dice : images.player2_dice;
        if (img) ctx.drawImage(img, 3, 420);
      }
    }

    // Player 1 (white) dice values - bottom left
    if (gs.currentPlayer === 'white' && gs.diceRolled) {
      const isDoubleRoll = gs.dice[0] === gs.dice[1];
      const doubleSpent = isDoubleRoll && gs.diceUsed.length === 4 && gs.diceUsed.every(Boolean);
      const d1 = (!isDoubleRoll && gs.diceUsed[0]) || doubleSpent ? images.blank_dice : images[`you_dice_${gs.dice[0]}`];
      const d2 = (!isDoubleRoll && gs.diceUsed[1]) || doubleSpent ? images.blank_dice : images[`you_dice_${gs.dice[1]}`];
      if (d1) ctx.drawImage(d1, 2, 650);
      if (d2) ctx.drawImage(d2, 2, 720);
    } else {
      if (images.blank_dice) {
        ctx.drawImage(images.blank_dice, 2, 650);
        ctx.drawImage(images.blank_dice, 2, 720);
      }
    }

    // Player 2 (black) dice values - top left
    if (gs.currentPlayer === 'black' && gs.diceRolled) {
      const isDoubleRoll = gs.dice[0] === gs.dice[1];
      const doubleSpent = isDoubleRoll && gs.diceUsed.length === 4 && gs.diceUsed.every(Boolean);
      const d1 = (!isDoubleRoll && gs.diceUsed[0]) || doubleSpent ? images.blank_cpu : images[`cpu_dice_${gs.dice[0]}`];
      const d2 = (!isDoubleRoll && gs.diceUsed[1]) || doubleSpent ? images.blank_cpu : images[`cpu_dice_${gs.dice[1]}`];
      if (d1) ctx.drawImage(d1, 2, 210);
      if (d2) ctx.drawImage(d2, 2, 285);
    } else {
      if (images.blank_cpu) {
        ctx.drawImage(images.blank_cpu, 2, 210);
        ctx.drawImage(images.blank_cpu, 2, 285);
      }
    }

    // Turn rolling animation dice
    if (gs.gamePhase === 'turnRoll' && turnRolling) {
      const wImg = images[`you_dice_${turnDiceW}`];
      const bImg = images[`cpu_dice_${turnDiceB}`];
      if (wImg) ctx.drawImage(wImg, 2, 650);
      if (bImg) ctx.drawImage(bImg, 2, 210);
    }

    if (gs.diceRolled && gs.dice[0] === gs.dice[1] && gs.currentPlayer) {
      const remainingMoves = gs.diceUsed.filter((used) => !used).length;
      ctx.fillStyle = 'rgba(0,0,0,0.72)';
      ctx.beginPath();
      ctx.roundRect(332, 20, 236, 34, 8);
      ctx.fill();
      ctx.fillStyle = '#f6e7c1';
      ctx.font = 'bold 18px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`Double ${gs.dice[0]} | Moves left: ${remainingMoves}`, 450, 43);
    }

    // Destination highlights for selected piece
    if (selectedFrom !== null && movesForSelected.length > 0) {
      for (const move of movesForSelected) {
        if (move.to === 'off') {
          if (gs.currentPlayer === 'white' && images.bearing_off_light) {
            ctx.drawImage(images.bearing_off_light, 838, 589);
          }
          if (gs.currentPlayer === 'black' && images.bearing_off_light) {
            ctx.drawImage(images.bearing_off_light, 838, 153);
          }
        } else {
          const lp = getPointLightPosition(move.to);
          if (move.to < 13 && images.destination_light) {
            ctx.drawImage(images.destination_light, lp[0], lp[1]);
          }
          if (move.to >= 13 && images.destination_light_bottom) {
            ctx.drawImage(images.destination_light_bottom, lp[0], lp[1]);
          }
        }
      }

      // Draw dice labels on destination highlights
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      for (const move of movesForSelected) {
        if (move.to === 'off') continue;
        const lp = getPointLightPosition(move.to);
        const labelX = lp[0] + 28;
        const labelY = move.to < 13 ? lp[1] - 4 : lp[1] + 68;
        // Background pill
        ctx.fillStyle = 'rgba(0,0,0,0.75)';
        const tw = ctx.measureText(`D${move.diceIndex + 1}`).width + 10;
        ctx.beginPath();
        ctx.roundRect(labelX - tw / 2, labelY - 12, tw, 16, 4);
        ctx.fill();
        ctx.fillStyle = '#ffd700';
        ctx.fillText(`D${move.diceIndex + 1}`, labelX, labelY);
      }
    }

    // No move message
    if (noMoveMsg) {
      ctx.fillStyle = 'rgba(21, 10, 6, 0.92)';
      ctx.strokeStyle = '#d6a85f';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(228, 50, 444, 76, 14);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#fff2cf';
      ctx.font = 'bold 24px Trebuchet MS';
      ctx.textAlign = 'center';
      ctx.fillText('No move possible', 450, 82);
      ctx.fillStyle = '#e7c98a';
      ctx.font = 'bold 16px Trebuchet MS';
      ctx.fillText('Turn will pass automatically in 10 seconds', 450, 109);
    }

    // First move message
    if (firstMoveMsg) {
      ctx.fillStyle = 'rgb(71, 40, 21)';
      ctx.font = '20px Arial';
      ctx.textAlign = 'left';
      ctx.fillText('Having first move', firstMoveMsg === 'white' ? 19 : 746, 80);
    }

    // Overlay screens (drawn last so they appear on top)
    if (gs.gamePhase === 'modeSelect') {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      ctx.fillStyle = 'rgba(48, 26, 16, 0.95)';
      ctx.strokeStyle = 'rgba(212, 165, 88, 0.65)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(180, 230 + MODE_SELECT_Y_SHIFT, 540, 650, 14);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.font = 'bold 42px Trebuchet MS';
      ctx.fillText('Choose Mode', 450, 370 + MODE_SELECT_Y_SHIFT);
      ctx.font = '20px Trebuchet MS';
      ctx.fillStyle = '#ffffff';
      ctx.fillText('Select how you want to play', 450, 408 + MODE_SELECT_Y_SHIFT);

      if (allowOnlineMode) {
        ctx.fillStyle = '#5a3118';
        ctx.beginPath();
        ctx.roundRect(MODE_ONLINE_BTN.x1, MODE_ONLINE_BTN.y1, MODE_ONLINE_BTN.x2 - MODE_ONLINE_BTN.x1, MODE_ONLINE_BTN.y2 - MODE_ONLINE_BTN.y1, 10);
        ctx.fill();
        ctx.strokeStyle = '#d7b16a';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.fillStyle = '#ffffff';
        ctx.font = `bold ${IS_MOBILE_MODE_SELECT_UI ? 32 : 28}px Trebuchet MS`;
        ctx.textBaseline = 'middle';
        ctx.fillText('Play Online', 450, (MODE_ONLINE_BTN.y1 + MODE_ONLINE_BTN.y2) / 2);
        ctx.textBaseline = 'alphabetic';
      }

      ctx.fillStyle = '#6f431f';
      ctx.beginPath();
      ctx.roundRect(MODE_CPU_BTN.x1, MODE_CPU_BTN.y1, MODE_CPU_BTN.x2 - MODE_CPU_BTN.x1, MODE_CPU_BTN.y2 - MODE_CPU_BTN.y1, 10);
      ctx.fill();
      ctx.strokeStyle = '#d7b16a';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${IS_MOBILE_MODE_SELECT_UI ? 32 : 28}px Trebuchet MS`;
      ctx.textBaseline = 'middle';
      ctx.fillText('Play with AI', 450, (MODE_CPU_BTN.y1 + MODE_CPU_BTN.y2) / 2);
      ctx.textBaseline = 'alphabetic';

      if (allowTournamentMode) {
        ctx.fillStyle = '#8b5a2b';
        ctx.beginPath();
        ctx.roundRect(MODE_LOCAL_BTN.x1, MODE_LOCAL_BTN.y1, MODE_LOCAL_BTN.x2 - MODE_LOCAL_BTN.x1, MODE_LOCAL_BTN.y2 - MODE_LOCAL_BTN.y1, 10);
        ctx.fill();
        ctx.strokeStyle = '#d7b16a';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.fillStyle = '#ffffff';
        ctx.font = `bold ${IS_MOBILE_MODE_SELECT_UI ? 32 : 28}px Trebuchet MS`;
        ctx.textBaseline = 'middle';
        ctx.fillText('Tournaments', 450, (MODE_LOCAL_BTN.y1 + MODE_LOCAL_BTN.y2) / 2);
        ctx.textBaseline = 'alphabetic';
      }

      if (modeNotice) {
        const noticeWidth = 430;
        const noticeHeight = 76;
        const noticeX = 235;
        const noticeY = 808 + MODE_SELECT_Y_SHIFT;

        ctx.fillStyle = 'rgba(88, 49, 26, 0.94)';
        ctx.strokeStyle = 'rgba(215, 177, 106, 0.9)';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.roundRect(noticeX, noticeY, noticeWidth, noticeHeight, 12);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = '#f4d9a2';
        ctx.font = 'bold 14px Trebuchet MS';
        ctx.textAlign = 'left';
        ctx.fillText('Mode Notice', noticeX + 16, noticeY + 22);

        ctx.fillStyle = '#f8ead0';
        ctx.font = '16px Trebuchet MS';
        drawWrappedCanvasText(ctx, modeNotice, noticeX + 16, noticeY + 46, noticeWidth - 32, 19);
        ctx.textAlign = 'center';
      }
    }
    if (gs.gamePhase === 'start' && images.start) ctx.drawImage(images.start, 0, 0);
    if (gs.gamePhase === 'gameOver') {
      if (gs.winner === 'white' && images.white_wins) ctx.drawImage(images.white_wins, 0, 0);
      if (gs.winner === 'black' && images.black_wins) ctx.drawImage(images.black_wins, 0, 0);
    }
  }, [allowOnlineMode, allowTournamentMode, images, gameState, hoverArea, selectedFrom, movesForSelected, movablePoints, turnRolling, turnDiceW, turnDiceB, noMoveMsg, firstMoveMsg, modeNotice, moveAnimation]);

  useEffect(() => {
    if (typeof onGamePhaseChange !== 'function') return;
    onGamePhaseChange(gameState?.gamePhase || '');
  }, [gameState?.gamePhase, onGamePhaseChange]);


  // ---- Animation loop ----
  useEffect(() => {
    if (!loaded) return;
    const loop = () => {
      render();
      animFrameRef.current = requestAnimationFrame(loop);
    };
    animFrameRef.current = requestAnimationFrame(loop);
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [loaded, render]);

  // ---- Loading screen ----
  if (!loaded) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '100vh', background: 'radial-gradient(circle at top, #3b1f10 0%, #160b06 70%)', color: '#f0d8a4',
        fontFamily: 'Arial', fontSize: '24px',
      }}>
        Loading Backgammon...
      </div>
    );
  }

  const openingLeftLabel = playMode === 'vsComputer' ? 'Computer' : 'Player 1';
  const openingRightLabel = playMode === 'vsComputer' ? 'You' : 'Player 2';
  const openingLeftValue = turnRolling || turnRollResult ? turnDiceW : '-';
  const openingRightValue = turnRolling || turnRollResult ? turnDiceB : '-';
  const openingResultText = !turnRollResult
    ? ''
    : turnRollResult.first === 'black'
      ? `${openingRightLabel} will start the first move.`
      : `${openingLeftLabel} will start the first move.`;

  function handleStartOpeningRoll() {
    if (gameState.gamePhase !== 'turnRoll' || turnRolling || turnRollResult) return;
    setTurnRollCount(0);
    setTurnDiceW(1);
    setTurnDiceB(1);
    setTurnRolling(true);
  }

  const diceCards = playMode === 'vsComputer' ? (
    <div style={styles.diceCardsRow}>
      <div style={{ ...styles.diceCard, ...(!gameState?.diceRolled ? styles.diceCardEmpty : {}), ...(visibleDiceValues.blurred ? styles.diceCardBlurred : {}), ...(diceTrayRolling ? styles.diceCardRolling : {}), ...(diceState.firstUsed ? styles.diceCardUsed : {}) }}>
        <div style={styles.dicePipWrap}>{renderDieFace(visibleDiceValues.first)}</div>
      </div>
      <div style={{ ...styles.diceCard, ...(!gameState?.diceRolled ? styles.diceCardEmpty : {}), ...(visibleDiceValues.blurred ? styles.diceCardBlurred : {}), ...(diceTrayRolling ? styles.diceCardRollingDelayed : {}), ...(diceState.secondUsed ? styles.diceCardUsed : {}) }}>
        <div style={styles.dicePipWrap}>{renderDieFace(visibleDiceValues.second)}</div>
      </div>
    </div>
  ) : null;
  const mobileDiceCards = playMode === 'vsComputer' ? (
    <div style={styles.mobileDiceOnlyRow}>
      <div style={{ ...styles.diceCard, ...styles.mobileDiceCard, ...(!gameState?.diceRolled ? styles.diceCardEmpty : {}), ...(visibleDiceValues.blurred ? styles.diceCardBlurred : {}), ...(diceState.firstUsed ? styles.diceCardUsed : {}), ...(diceTrayRolling ? styles.diceCardRolling : {}) }}>
        <div style={styles.dicePipWrap}>{renderDieFace(visibleDiceValues.first)}</div>
      </div>
      <div style={styles.mobileTurnCenterBlock}>
        <div style={styles.mobileTurnCenterTitle}>{turnLabel}</div>
        {gameState?.diceRolled && diceState.isDouble && (
          <div style={styles.mobileTurnCenterMeta}>{diceState.remainingMoves} left</div>
        )}
      </div>
      <div style={{ ...styles.diceCard, ...styles.mobileDiceCard, ...(!gameState?.diceRolled ? styles.diceCardEmpty : {}), ...(visibleDiceValues.blurred ? styles.diceCardBlurred : {}), ...(diceState.secondUsed ? styles.diceCardUsed : {}), ...(diceTrayRolling ? styles.diceCardRollingDelayed : {}) }}>
        <div style={styles.dicePipWrap}>{renderDieFace(visibleDiceValues.second)}</div>
      </div>
    </div>
  ) : null;

  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      height: '100dvh', background: 'radial-gradient(circle at top, #3b1f10 0%, #160b06 70%)',
      position: 'relative',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      WebkitTouchCallout: 'none',
      WebkitTapHighlightColor: 'transparent',
    }}>
      <style>{`
        @keyframes diceTrayRollA {
          0% { transform: translateY(0) rotate(0deg) scale(1); }
          30% { transform: translateY(-8px) rotate(-8deg) scale(1.04); }
          65% { transform: translateY(4px) rotate(7deg) scale(0.98); }
          100% { transform: translateY(0) rotate(0deg) scale(1); }
        }
        @keyframes diceTrayRollB {
          0% { transform: translateY(0) rotate(0deg) scale(1); }
          28% { transform: translateY(-10px) rotate(9deg) scale(1.05); }
          68% { transform: translateY(5px) rotate(-7deg) scale(0.98); }
          100% { transform: translateY(0) rotate(0deg) scale(1); }
        }
        @keyframes rollReadyPulse {
          0% { transform: scale(1); box-shadow: 0 14px 28px rgba(102,46,16,0.35); }
          50% { transform: scale(1.04); box-shadow: 0 0 0 4px rgba(214,167,83,0.18), 0 18px 34px rgba(186,112,34,0.5); }
          100% { transform: scale(1); box-shadow: 0 14px 28px rgba(102,46,16,0.35); }
        }
      `}</style>
      {typeof onExit === 'function' && (
        <button
          type="button"
          onClick={onExit}
          style={{
            position: 'fixed',
            top: 14,
            right: 14,
            zIndex: 2200,
            border: '1px solid #d7b16a',
            background: '#643030',
            color: '#f6e7c1',
            padding: '9px 12px',
            borderRadius: 10,
            cursor: 'pointer',
            fontWeight: 700,
            boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
          }}
        >
          Exit
        </button>
      )}
      {playMode && (
        <button
          type="button"
          onClick={handleBackPress}
          style={{
            position: 'fixed',
            top: 14,
            left: 14,
            zIndex: 2200,
            border: '1px solid #d7b16a',
            background: '#7a4c22',
            color: '#f6e7c1',
            padding: '9px 12px',
            borderRadius: 10,
            cursor: 'pointer',
            fontWeight: 700,
            boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
            fontSize: isMobileViewport ? 13 : 'inherit',
          }}
        >
          Back
        </button>
      )}
      {isAiPlayingScreen && isMobileViewport && typeof onToggleFullscreen === 'function' && (
        <button
          type="button"
          onClick={onToggleFullscreen}
          title={isFullscreen ? 'Exit Full Screen' : 'Play Full Screen'}
          style={styles.mobileFullscreenIconBtn}
        >
          ⛶
        </button>
      )}
      {gameState.gamePhase === 'turnRoll' && (
        <LocalOpeningRollModal
          leftLabel={openingLeftLabel}
          rightLabel={openingRightLabel}
          leftValue={openingLeftValue}
          rightValue={openingRightValue}
          rolling={turnRolling}
          resultText={openingResultText}
          onRoll={handleStartOpeningRoll}
        />
      )}
      {showQuitConfirm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2300,
            display: 'grid',
            placeItems: 'center',
            background: 'rgba(0,0,0,0.58)',
            padding: 16,
          }}
        >
          <div
            style={{
              width: 'min(420px, calc(100vw - 32px))',
              background: 'rgba(33,18,11,0.97)',
              border: '1px solid rgba(215,177,106,0.8)',
              borderRadius: 14,
              padding: '20px 18px',
              boxShadow: '0 14px 36px rgba(0,0,0,0.45)',
              color: '#f6e7c1',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 26, fontWeight: 800, marginBottom: 10 }}>Quit Match?</div>
            <div style={{ fontSize: 16, lineHeight: 1.5, color: '#e7c98a', marginBottom: 18 }}>
              Are you sure you want to quit this ongoing match?
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setShowQuitConfirm(false)}
                style={{
                  minWidth: 120,
                  border: '1px solid #d7b16a',
                  background: '#3a2314',
                  color: '#f6e7c1',
                  padding: '10px 14px',
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontWeight: 700,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={resetToModeSelect}
                style={{
                  minWidth: 120,
                  border: '1px solid #d7b16a',
                  background: '#643030',
                  color: '#f6e7c1',
                  padding: '10px 14px',
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontWeight: 700,
                }}
              >
                Quit
              </button>
            </div>
          </div>
        </div>
      )}
      {isAiPlayingScreen && !isMobileViewport && (
        <div style={{ ...styles.diceTrayCard, ...styles.diceTrayDesktop, ...(diceTrayRolling ? styles.diceTrayCardRolling : {}) }}>
          <div style={styles.diceTrayHeader}>
            <div style={styles.diceTrayHeaderText}>
              <div style={styles.diceTrayTurn}>{turnLabel}</div>
              <div style={styles.diceTrayEyebrow}>{diceTrayTitle}</div>
            </div>
            {gameState?.diceRolled && diceState.isDouble && (
              <div style={styles.diceTrayPill}>{diceState.remainingMoves} left</div>
            )}
          </div>
          <div style={styles.diceTrayBodyDesktop}>
            {diceCards}
          </div>
          <div style={styles.diceTrayDesktopActionRow}>
            <button
              type="button"
              onClick={handleRollAction}
              disabled={!canRollDice}
              style={{
                ...styles.rollActionBtn,
                ...(canRollDice ? styles.rollActionBtnReady : {}),
                ...(!canRollDice ? styles.buttonDisabled : {}),
              }}
            >
              Roll Dice
            </button>
          </div>
        </div>
      )}
      <canvas
        ref={canvasRef}
        width={CANVAS_W}
        height={CANVAS_H}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onMouseDown={(e) => e.preventDefault()}
        onPointerDown={(e) => e.preventDefault()}
        style={{
          maxWidth: '100vw',
          maxHeight: '100vh',
          cursor: 'pointer',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
          outline: 'none',
          transform: 'none',
        }}
      />
      {isAiPlayingScreen && isMobileViewport && (
        <div style={styles.mobileDiceTopCard}>
          {mobileDiceCards}
        </div>
      )}
      {isAiPlayingScreen && isMobileViewport && (
        <div style={styles.mobileRollBottomBar}>
          <button
            type="button"
            onClick={handleRollAction}
            disabled={!canRollDice}
            style={{
              ...styles.rollActionBtn,
              ...styles.rollActionBtnMobile,
              ...styles.mobileRollBottomBtn,
              ...(canRollDice ? styles.rollActionBtnReady : {}),
              ...(!canRollDice ? styles.buttonDisabled : {}),
            }}
          >
            Roll Dice
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  buttonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
    filter: 'grayscale(0.15)',
  },
  mobileFullscreenIconBtn: {
    position: 'fixed',
    top: 14,
    right: 14,
    width: 48,
    height: 48,
    borderRadius: 12,
    border: '1px solid #d7b16a',
    background: '#7a4c22',
    color: '#f6e7c1',
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    fontWeight: 800,
    boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2200,
  },
  diceTrayDesktop: {
    position: 'fixed',
    right: 36,
    top: 296,
    bottom: 'auto',
    left: 'auto',
    transform: 'none',
    zIndex: 2100,
  },
  diceTrayCard: {
    width: 'min(300px, calc(100vw - 40px))',
    display: 'grid',
    gap: 10,
    padding: 12,
    borderRadius: 16,
    border: '1px solid rgba(215,177,106,0.28)',
    background: 'linear-gradient(135deg, rgba(44,24,13,0.96), rgba(26,14,8,0.98))',
    boxShadow: '0 18px 36px rgba(0,0,0,0.28)',
    overflow: 'hidden',
  },
  diceTrayCardRolling: {
    boxShadow: '0 20px 42px rgba(213,160,81,0.22)',
  },
  diceTrayHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
  },
  diceTrayHeaderText: {
    display: 'grid',
    gap: 3,
    minWidth: 0,
  },
  diceTrayTurn: {
    color: '#d7b16a',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    lineHeight: 1,
  },
  diceTrayEyebrow: {
    color: '#f6e7c1',
    fontSize: 20,
    fontWeight: 800,
    lineHeight: 1.1,
  },
  diceTrayPill: {
    borderRadius: 999,
    padding: '8px 12px',
    border: '1px solid rgba(215,177,106,0.32)',
    background: 'rgba(122,76,34,0.28)',
    color: '#ffe6b6',
    fontSize: 12,
    fontWeight: 800,
    whiteSpace: 'nowrap',
  },
  diceTrayBodyDesktop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 18,
    flexWrap: 'nowrap',
  },
  diceTrayDesktopActionRow: {
    display: 'flex',
    justifyContent: 'stretch',
    width: '100%',
  },
  diceCardsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 22,
    width: '100%',
    flexWrap: 'nowrap',
  },
  diceCard: {
    width: 58,
    height: 58,
    borderRadius: 16,
    border: '1px solid rgba(255,245,228,0.18)',
    background: 'linear-gradient(180deg, #fff6ea 0%, #efd8b7 100%)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.75), 0 12px 28px rgba(0,0,0,0.22)',
    display: 'grid',
    placeItems: 'center',
    position: 'relative',
    transform: 'translateY(0)',
    flexShrink: 0,
  },
  diceCardRolling: {
    animation: 'diceTrayRollA 0.45s ease',
  },
  diceCardRollingDelayed: {
    animation: 'diceTrayRollB 0.52s ease',
  },
  diceCardEmpty: {
    opacity: 0.55,
    background: 'linear-gradient(180deg, #f3e6d3 0%, #dbc6a4 100%)',
  },
  diceCardBlurred: {
    filter: 'saturate(0.75)',
  },
  diceCardUsed: {
    opacity: 0.45,
    filter: 'grayscale(0.3)',
  },
  dicePipWrap: {
    position: 'relative',
    width: 58,
    height: 58,
  },
  dicePip: {
    position: 'absolute',
    width: 8,
    height: 8,
    borderRadius: 999,
    background: '#352113',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)',
  },
  rollActionBtn: {
    width: '100%',
    minWidth: 118,
    border: '1px solid rgba(255,220,160,0.36)',
    background: 'linear-gradient(135deg, #c97a2a, #9a511b)',
    color: '#fff7ef',
    borderRadius: 14,
    padding: '9px 12px',
    cursor: 'pointer',
    fontWeight: 800,
    fontSize: 13,
    boxShadow: '0 14px 28px rgba(102,46,16,0.35)',
    flexShrink: 0,
    lineHeight: 1,
  },
  rollActionBtnReady: {
    animation: 'rollReadyPulse 1.1s ease-in-out infinite',
    borderColor: 'rgba(255,226,167,0.88)',
  },
  rollActionBtnMobile: {
    width: 'auto',
    minWidth: 132,
  },
  mobileDiceTopCard: {
    position: 'fixed',
    left: 8,
    right: 8,
    top: 68,
    display: 'grid',
    gap: 6,
    padding: 8,
    borderRadius: 10,
    border: '1px solid rgba(215,177,106,0.18)',
    background: 'rgba(27,14,8,0.9)',
    zIndex: 2100,
    boxShadow: '0 12px 26px rgba(0,0,0,0.22)',
    minHeight: 86,
  },
  mobileDiceOnlyRow: {
    display: 'grid',
    gridTemplateColumns: '58px 1fr 58px',
    alignItems: 'center',
    justifyItems: 'center',
    columnGap: 14,
    width: '100%',
  },
  mobileRollBottomBar: {
    position: 'fixed',
    left: 8,
    right: 8,
    bottom: 8,
    display: 'flex',
    justifyContent: 'center',
    pointerEvents: 'none',
    zIndex: 2100,
  },
  mobileRollBottomBtn: {
    width: 'min(290px, calc(100vw - 28px))',
    minHeight: 52,
    padding: '14px 22px',
    fontSize: 16,
    pointerEvents: 'auto',
  },
  mobileDiceCard: {
    opacity: 1,
  },
  mobileTurnCenterBlock: {
    minWidth: 0,
    width: '100%',
    display: 'grid',
    gap: 4,
    justifyItems: 'center',
    textAlign: 'center',
    flexShrink: 0,
  },
  mobileTurnCenterTitle: {
    color: '#f6e7c1',
    fontSize: 17,
    fontWeight: 900,
    lineHeight: 1.1,
  },
  mobileTurnCenterMeta: {
    color: '#d7b16a',
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1,
  },
  mobileDiceBottomCard: {
    position: 'fixed',
    left: 8,
    right: 8,
    bottom: 8,
    display: 'grid',
    gap: 8,
    padding: 8,
    borderRadius: 10,
    border: '1px solid rgba(215,177,106,0.18)',
    background: 'rgba(27,14,8,0.9)',
    zIndex: 2100,
    boxShadow: '0 12px 26px rgba(0,0,0,0.22)',
  },
};
